import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Container, Paper, Button, Box,
  IconButton, Avatar, Menu, MenuItem, Grid, TextField, Divider,
  CircularProgress, Autocomplete, Chip, RadioGroup, FormControlLabel, Radio, FormControl, FormLabel,
} from '@mui/material';
import { LocalHospital, ArrowBack, ExitToApp, Print, Assignment, Science } from '@mui/icons-material';
import { printSettingsService, appointmentService, prescriptionService, investigationService, dischargeSummaryService, authService } from '../../services/api';

const DischargeSummaryPage = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);
  const [printSettings, setPrintSettings] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [investigationsList, setInvestigationsList] = useState([]);
  const [selectedDischargeInvestigations, setSelectedDischargeInvestigations] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    pmlcNo: '',
    swdo: '',
    address: '',
    dodTime: '',
    dischargeType: 'Normal Discharge',
    diagnosis: '',
    history: '',
    conditionOnAdmission: '',
    investigationsOnAdmission: '',
    investigationsOnDischarge: '',
    procedureDone: '',
    treatmentGiven: '',
    conditionOnDischarge: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [psRes, apptRes, invRes] = await Promise.all([
          printSettingsService.getSettings(),
          appointmentService.getAppointment(appointmentId),
          investigationService.getAllInvestigations(),
        ]);
        setInvestigationsList(invRes.data || []);
        setPrintSettings(psRes.data);
        setAppointment(apptRes.data);

        const appt = apptRes.data;
        const admissionDate = appt?.appointmentDate ? new Date(appt.appointmentDate) : null;

        // Fetch all prescriptions for this patient and filter from admission date to today
        let investigationsText = '';
        if (appt?.patientId && admissionDate) {
          const presRes = await prescriptionService.getPrescriptions(appt.patientId);
          const prescriptions = presRes.data || [];
          const today = new Date();

          // Filter prescriptions between admission date and today
          const relevant = prescriptions.filter((p) => {
            const pDate = new Date(p.date);
            return pDate >= admissionDate && pDate <= today;
          });

          // Collect unique investigation names
          const names = new Set();
          relevant.forEach((p) => {
            (p.prescriptionInvestigations || []).forEach((pi) => {
              if (pi.investigation?.name) names.add(pi.investigation.name);
            });
          });

          investigationsText = Array.from(names).join('\n');
        }

        setForm((prev) => ({
          ...prev,
          address: appt?.patient?.address || '',
          investigationsOnAdmission: investigationsText,
        }));
      } catch (e) {
        console.error('Failed to load discharge summary data', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [appointmentId]);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handlePrintAndSave = async () => {
    setSaving(true);
    try {
      await dischargeSummaryService.save({
        appointmentId:             parseInt(appointmentId),
        patientId:                 appointment?.patientId,
        pmlcNo:                    form.pmlcNo,
        swdo:                      form.swdo,
        address:                   form.address,
        dodTime:                   form.dodTime ? new Date(form.dodTime).toISOString() : null,
        consultantName:            consultantName,
        diagnosis:                 form.diagnosis,
        history:                   form.history,
        conditionOnAdmission:      form.conditionOnAdmission,
        investigationsOnAdmission: form.investigationsOnAdmission,
        investigationsOnDischarge: selectedDischargeInvestigations.join(', '),
        procedureDone:             form.procedureDone,
        treatmentGiven:            form.treatmentGiven,
        conditionOnDischarge:      form.conditionOnDischarge,
        dischargeType:             form.dischargeType,
      });

      // Mark the IPD appointment as Completed so it leaves the IPD list and becomes billable
      try {
        await appointmentService.updateAppointment(parseInt(appointmentId), {
          id:              appointment.id,
          patientId:       appointment.patientId,
          doctorId:        appointment.doctorId,
          appointmentDate: appointment.appointmentDate,
          appointmentTime: appointment.appointmentTime,
          type:            appointment.type,
          notes:           appointment.notes,
          status:          'Completed',
          bp:              appointment.bp,
          pulse:           appointment.pulse,
          weight:          appointment.weight,
          spO2:            appointment.spO2 || appointment.spo2,
          gestationalAge:  appointment.gestationalAge,
        });
      } catch (e) {
        console.error('Failed to mark appointment as Completed', e);
      }
    } catch (e) {
      console.error('Failed to save discharge summary', e);
    } finally {
      setSaving(false);
      window.print();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const patient = appointment?.patient;
  const patientAge = calculateAge(patient?.dateOfBirth);
  // Format for datetime-local input: yyyy-MM-ddTHH:mm
  const doaDateTimeLocal = appointment
    ? `${appointment.appointmentDate.substring(0, 10)}T${appointment.appointmentTime || '00:00'}`
    : '';
  // Format for print (both DOA and DOD)
  const formatForPrint = (val) => {
    if (!val) return '';
    const d = new Date(val);
    return d.toLocaleDateString('en-IN') + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };
  const consultantName = user ? `Dr. ${user.firstName} ${user.lastName}` : '';

  const isPrePrinted = (printSettings?.letterheadType || printSettings?.LetterheadType || 'Pre-Printed Letterhead') === 'Pre-Printed Letterhead';
  const mt = printSettings?.marginTop    ?? printSettings?.MarginTop    ?? 10;
  const mb = printSettings?.marginBottom ?? printSettings?.MarginBottom ?? 10;
  const ml = printSettings?.marginLeft   ?? printSettings?.MarginLeft   ?? 10;
  const mr = printSettings?.marginRight  ?? printSettings?.MarginRight  ?? 10;

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media screen {
          #discharge-print-area { display: none !important; }
        }
        @media print {
          @page { margin: ${mt}mm ${mr}mm ${mb}mm ${ml}mm; }
          #discharge-screen-area { display: none !important; }
          #discharge-print-area {
            display: block !important;
            visibility: visible !important;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 16px;
            box-sizing: border-box;
            font-family: Arial, sans-serif;
          }
          #discharge-print-area * { visibility: visible !important; }
          .ds-table { width: 100%; border-collapse: collapse; font-size: 13px; }
          .ds-table td { border: 1px solid #1565c0; padding: 4px 8px; vertical-align: top; }
          .ds-label { color: #1565c0; font-weight: 600; white-space: nowrap; }
          .ds-section-header {
            background-color: #e3f2fd !important; -webkit-print-color-adjust: exact; print-color-adjust: exact;
            color: #1565c0; font-weight: 700;
            text-align: center; padding: 6px; border: 1px solid #1565c0;
            font-size: 15px; letter-spacing: 1px;
          }
          .ds-tall td { height: 80px; }
          .ds-medium td { height: 60px; }
        }
      `}</style>

      {/* ── Screen View ── */}
      <div id="discharge-screen-area" className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <AppBar position="static" className="medical-gradient no-print">
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => navigate('/ipd')} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
            <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
              Discharge Summary
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrintAndSave}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
              >
                Print
              </Button>
              <Typography variant="body1">{user?.firstName}</Typography>
              <IconButton size="large" onClick={(e) => setAnchorEl(e.currentTarget)} color="inherit">
                <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                  <ExitToApp sx={{ mr: 1 }} /> Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: 4 }}>

          {/* Patient Info Card */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Assignment sx={{ fontSize: 32, color: '#9c27b0', mr: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Patient Information</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Patient ID" value={patient?.id || ''} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="IPD No." value={appointment?.id || ''} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="PMLC No." name="pmlcNo" value={form.pmlcNo} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Patient Name" value={patient ? `${patient.firstName} ${patient.lastName}` : ''} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Age" value={patientAge || ''} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Sex" value={patient?.gender || ''} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="S/W/D/O" name="swdo" value={form.swdo} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Address" name="address" value={form.address} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Discharge Type</FormLabel>
                  <RadioGroup
                    row
                    name="dischargeType"
                    value={form.dischargeType}
                    onChange={handleChange}
                  >
                    <FormControlLabel value="Normal Discharge" control={<Radio />} label="Normal Discharge" />
                    <FormControlLabel value="DAMA" control={<Radio />} label="DAMA" />
                    <FormControlLabel value="Abscond" control={<Radio />} label="Abscond" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Admission / Discharge Info */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Admission & Discharge Details</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="DOA & Time"
                  type="datetime-local"
                  value={doaDateTimeLocal}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="DOD & Time"
                  name="dodTime"
                  type="datetime-local"
                  value={form.dodTime}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Consultant's Name" value={consultantName} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
            </Grid>
          </Paper>

          {/* Clinical Details */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Clinical Details</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Diagnosis" name="diagnosis" value={form.diagnosis} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="History" name="history" value={form.history} onChange={handleChange} multiline rows={3} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Condition on Admission" name="conditionOnAdmission" value={form.conditionOnAdmission} onChange={handleChange} multiline rows={2} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Investigations on Admission" name="investigationsOnAdmission" value={form.investigationsOnAdmission} onChange={handleChange} multiline rows={3} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={investigationsList.map((i) => i.name)}
                  value={selectedDischargeInvestigations}
                  onChange={(_, newValue) => setSelectedDischargeInvestigations(newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={index}
                        icon={<Science sx={{ fontSize: 14 }} />}
                        label={option}
                        size="small"
                        color="primary"
                        variant="outlined"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Investigations on Discharge"
                      placeholder="Type or select investigation..."
                      helperText="Select from list or type a custom investigation"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Procedure Done" name="procedureDone" value={form.procedureDone} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Treatment Given" name="treatmentGiven" value={form.treatmentGiven} onChange={handleChange} multiline rows={3} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Condition on Discharge" name="conditionOnDischarge" value={form.conditionOnDischarge} onChange={handleChange} multiline rows={2} />
              </Grid>
            </Grid>
          </Paper>

          {/* Bottom Print Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/ipd')}>Back to IPD</Button>
            <Button variant="contained" startIcon={<Print />} onClick={handlePrintAndSave} disabled={saving} sx={{ bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}>
              {saving ? 'Saving...' : 'Print Discharge Summary'}
            </Button>
          </Box>

        </Container>
      </div>

      {/* ── Print Area ── */}
      <div id="discharge-print-area">
        {/* Hospital Header — only shown when not using pre-printed letterhead */}
        {!isPrePrinted && (
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 20 }}>{printSettings?.hospitalName || 'Hospital Name'}</div>
            {printSettings?.address && <div style={{ color: '#1565c0', fontWeight: 600, fontSize: 13 }}>{printSettings.address}</div>}
            {printSettings?.phone && <div style={{ color: '#1565c0', fontWeight: 600, fontSize: 13 }}>Contact: {printSettings.phone}</div>}
          </div>
        )}

        <table className="ds-table">
          <tbody>
            <tr><td colSpan={6} className="ds-section-header">Discharge Summary</td></tr>
            <tr>
              <td colSpan={6} style={{ textAlign: 'center', padding: '6px 8px' }}>
                <span className="ds-label">Discharge Type: </span>{form.dischargeType}
              </td>
            </tr>
            <tr>
              <td style={{ width: '12%' }}><span className="ds-label">Patient ID:</span></td>
              <td style={{ width: '22%' }}>{patient?.id || ''}</td>
              <td style={{ width: '10%' }}><span className="ds-label">IPD No.:</span></td>
              <td style={{ width: '22%' }}>{appointment?.id || ''}</td>
              <td style={{ width: '12%' }}><span className="ds-label">PMLC No.:</span></td>
              <td style={{ width: '22%' }}>{form.pmlcNo}</td>
            </tr>
            <tr>
              <td><span className="ds-label">Patient Name:</span></td>
              <td>{patient ? `${patient.firstName} ${patient.lastName}` : ''}</td>
              <td><span className="ds-label">Age:</span></td>
              <td>{patientAge || ''}</td>
              <td><span className="ds-label">Sex:</span></td>
              <td>{patient?.gender || ''}</td>
            </tr>
            <tr>
              <td><span className="ds-label">S/W/D/O:</span></td>
              <td colSpan={5}>{form.swdo}</td>
            </tr>
            <tr>
              <td><span className="ds-label">Address:</span></td>
              <td colSpan={5} style={{ minHeight: 40 }}>{form.address}</td>
            </tr>
            <tr>
              <td><span className="ds-label">DOA & Time:</span></td>
              <td colSpan={2}>{formatForPrint(doaDateTimeLocal)}</td>
              <td><span className="ds-label">DOD & Time:</span></td>
              <td colSpan={2}>{formatForPrint(form.dodTime)}</td>
            </tr>
            <tr>
              <td><span className="ds-label">Consultant's Name:</span></td>
              <td colSpan={5}>{consultantName}</td>
            </tr>
            <tr>
              <td><span className="ds-label">Diagnosis:</span></td>
              <td colSpan={5}>{form.diagnosis}</td>
            </tr>
            <tr className="ds-tall">
              <td><span className="ds-label">History:</span></td>
              <td colSpan={5}>{form.history}</td>
            </tr>
            <tr className="ds-medium">
              <td><span className="ds-label">Condition on Admission:</span></td>
              <td colSpan={5}>{form.conditionOnAdmission}</td>
            </tr>
            <tr className="ds-tall">
              <td colSpan={3}>
                <span className="ds-label">Investigations on Admission:</span>
                <div style={{ minHeight: 60 }}>{form.investigationsOnAdmission}</div>
              </td>
              <td colSpan={3}>
                <span className="ds-label">Investigations on Discharge:</span>
                <div style={{ minHeight: 60 }}>{selectedDischargeInvestigations.join(', ')}</div>
              </td>
            </tr>
            <tr>
              <td><span className="ds-label">Procedure Done:</span></td>
              <td colSpan={5}>{form.procedureDone}</td>
            </tr>
            <tr className="ds-medium">
              <td><span className="ds-label">Treatment Given:</span></td>
              <td colSpan={5}>{form.treatmentGiven}</td>
            </tr>
            <tr className="ds-medium">
              <td><span className="ds-label">Condition on Discharge:</span></td>
              <td colSpan={5}>{form.conditionOnDischarge}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

export default DischargeSummaryPage;
